/**
 * Mapper de pedidos — Portal Cliente Menuee
 *
 * Responsabilidad:
 * - Transformar carrito + checkout → payload E6 (sin montos).
 * - Transformar respuestas E6 → vistas de confirmación / tracking.
 *
 * Qué nunca debe hacer:
 * - Invocar Supabase ni RPC.
 * - Incluir subtotal, total, delivery ni propinas en payload de creación.
 * - Recalcular montos oficiales al crear (solo leerlos de la respuesta E6).
 */

/**
 * @typedef {import('../hooks/useCarrito').CarritoItem} CarritoItem
 */

/**
 * @typedef {Object} PedidoConfirmacionView
 * @property {string} pedidoId
 * @property {string|null} codigoPedido
 * @property {number|null} numeroOperativo
 * @property {string|null} estado
 * @property {string|null} canal
 * @property {number|null} subtotal
 * @property {number|null} montoDelivery
 * @property {number|null} total
 * @property {number|null} itemsCount
 * @property {string|null} [creadoEn]
 * @property {{ nombre: string, cantidad: number, comentario?: string|null, extras?: { nombre: string }[] }[]} [items]
 */

/**
 * @typedef {Object} PedidoE6PayloadBase
 * @property {'delivery'|'retiro'} canal
 * @property {{ nombre: string, telefono: string }} cliente
 * @property {Object|null} direccion
 * @property {Array<{ producto_sucursal_id: string, cantidad: number, comentario: string|null }>} items
 */

import {
  resolverProductoSucursalId,
  validarCarritoOpciones,
} from "./productoMapper";

const STORAGE_ULTIMO_PEDIDO = "menuee_ultimo_pedido";

/**
 * @param {string} canal
 * @returns {'delivery'|'retiro'}
 */
export function normalizarCanalMenuee(canal) {
  if (canal === "delivery" || canal === "retiro") {
    return canal;
  }
  if (canal === "domicilio") {
    return "delivery";
  }
  throw new Error(`normalizarCanalMenuee: canal no soportado (${canal})`);
}

/**
 * Arma p_notas (comentario general operativo) sin montos.
 *
 * @param {Object} [params]
 * @param {string|null} [params.horario]
 * @param {string|null} [params.referencias]
 * @param {string|null} [params.comentarios]
 * @param {string|null} [params.comentarioGeneral]
 * @returns {string|null}
 */
export function buildNotasPedido({
  horario = null,
  referencias = null,
  comentarios = null,
  comentarioGeneral = null,
} = {}) {
  const partes = [];

  if (horario) {
    partes.push(`Horario: ${String(horario).trim()}`);
  }
  if (referencias) {
    partes.push(`Referencias: ${String(referencias).trim()}`);
  }
  if (comentarios) {
    partes.push(String(comentarios).trim());
  }
  if (comentarioGeneral) {
    partes.push(String(comentarioGeneral).trim());
  }

  const texto = partes.filter(Boolean).join(" | ");
  return texto || null;
}

/**
 * @param {Object} params
 * @param {CarritoItem[]} params.carrito
 * @param {'delivery'|'retiro'|string} params.canal
 * @param {{ nombre: string, telefono: string }} params.cliente
 * @param {Object|null} [params.direccion]
 * @returns {PedidoE6PayloadBase}
 */
export function mapCarritoToE6Payload({ carrito, canal, cliente, direccion = null }) {
  const canalMenuee = normalizarCanalMenuee(canal);

  const nombre = String(cliente?.nombre || "").trim();
  const telefono = String(cliente?.telefono || "").trim();

  if (!nombre || !telefono) {
    throw new Error("mapCarritoToE6Payload: cliente.nombre y cliente.telefono son obligatorios");
  }

  if (!carrito?.length) {
    throw new Error("mapCarritoToE6Payload: el carrito está vacío");
  }

  const errorOpciones = validarCarritoOpciones(carrito);
  if (errorOpciones) {
    throw new Error(`mapCarritoToE6Payload: ${errorOpciones}`);
  }

  const items = [];

  for (const item of carrito) {
    items.push({
      producto_sucursal_id: item.productoSucursalId,
      cantidad: item.cantidad,
      comentario: item.comentario ?? null,
    });

    for (const extra of item.extras || []) {
      const productoSucursalId = resolverProductoSucursalId(extra);
      if (!productoSucursalId) continue;

      items.push({
        producto_sucursal_id: productoSucursalId,
        cantidad: item.cantidad,
        comentario: null,
      });
    }
  }

  let direccionNormalizada = null;

  if (canalMenuee === "delivery") {
    const texto =
      direccion?.texto ??
      direccion?.direccion ??
      direccion?.direccion_entrega ??
      null;

    if (!texto || !String(texto).trim()) {
      throw new Error("mapCarritoToE6Payload: dirección obligatoria para delivery");
    }

    direccionNormalizada = {
      texto: String(texto).trim(),
      lat: direccion?.lat ?? direccion?.cliente_lat ?? null,
      lon: direccion?.lon ?? direccion?.cliente_lon ?? null,
      referencias: direccion?.referencias ?? null,
      comentarios: direccion?.comentarios ?? null,
    };
  }

  return {
    canal: canalMenuee,
    cliente: { nombre, telefono },
    direccion: direccionNormalizada,
    items,
  };
}

/**
 * Parámetros rpc_crear_pedido (sin idempotency_key; la agrega rpcClient).
 *
 * @param {Object} params
 * @param {string} params.sucursalId
 * @param {PedidoE6PayloadBase} params.payload
 * @param {string} [params.estadoInicial='pendiente']
 * @param {string|null} [params.notas]
 * @returns {Object}
 */
export function mapE6PayloadToRpcParams({
  sucursalId,
  payload,
  estadoInicial = "pendiente",
  notas = null,
}) {
  const rpcParams = {
    p_sucursal_id: sucursalId,
    p_canal: payload.canal,
    p_items: payload.items.map((item) => ({
      producto_sucursal_id: item.producto_sucursal_id,
      cantidad: item.cantidad,
      comentario: item.comentario,
    })),
    p_mesa_id: null,
    p_estado_inicial: estadoInicial,
    p_cliente_nombre: payload.cliente.nombre,
    p_cliente_telefono: payload.cliente.telefono,
    p_notas: notas,
  };

  if (payload.canal === "delivery" && payload.direccion) {
    rpcParams.p_direccion_entrega = payload.direccion.texto;
    rpcParams.p_cliente_lat = payload.direccion.lat;
    rpcParams.p_cliente_lon = payload.direccion.lon;
  }

  return rpcParams;
}

/**
 * Normaliza datos de ModalDelivery / ModalRetiro al input de createPedido.
 *
 * @param {Object} params
 * @param {Object} params.datosCheckout
 * @param {CarritoItem[]} params.carrito
 * @param {string} params.sucursalId
 * @param {string} params.modoEntrega
 * @param {string|null} [params.horarioSeleccionado]
 */
export function mapCheckoutFormToCreatePedidoInput({
  datosCheckout,
  carrito,
  sucursalId,
  modoEntrega,
  horarioSeleccionado = null,
}) {
  const canal = datosCheckout?.canal
    ? normalizarCanalMenuee(datosCheckout.canal)
    : normalizarCanalMenuee(modoEntrega);

  const cliente = datosCheckout?.cliente || {
    nombre: datosCheckout?.nombre,
    telefono: datosCheckout?.telefono,
  };

  let direccion = null;

  if (canal === "delivery") {
    if (datosCheckout?.direccion && typeof datosCheckout.direccion === "object") {
      direccion = datosCheckout.direccion;
    } else {
      direccion = {
        texto: datosCheckout?.direccion,
        lat: datosCheckout?.coordenadas?.lat ?? null,
        lon: datosCheckout?.coordenadas?.lon ?? null,
        referencias: datosCheckout?.referencias ?? null,
        comentarios: datosCheckout?.comentarios ?? null,
      };
    }
  }

  return {
    sucursalId,
    carrito,
    canal,
    cliente,
    direccion,
    horario: horarioSeleccionado || null,
  };
}

function pareceUuid(valor) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(valor || "").trim()
  );
}

/**
 * @param {Object|null|undefined} e6Data
 * @returns {number|null}
 */
function extraerNumeroOperativoE6(e6Data) {
  const raw =
    e6Data?.numero_operativo ??
    e6Data?.pedido?.numero_operativo ??
    e6Data?.data?.numero_operativo ??
    e6Data?.numero ??
    e6Data?.codigo_operativo ??
    e6Data?.folio ??
    e6Data?.numero_pedido ??
    null;

  if (raw == null || raw === "") {
    return null;
  }

  if (pareceUuid(raw)) {
    return null;
  }

  const numero = Number(raw);
  return Number.isFinite(numero) && numero > 0 ? numero : null;
}

export function mapPedidoE6ResponseToView(e6Data) {
  if (!e6Data?.pedido_id) {
    return null;
  }

  const numeroOperativo = extraerNumeroOperativoE6(e6Data);

  return {
    pedidoId: e6Data.pedido_id,
    codigoPedido:
      numeroOperativo != null ? String(numeroOperativo) : null,
    numeroOperativo,
    estado: e6Data.estado ?? "pendiente",
    canal: e6Data.canal ?? "",
    subtotal: e6Data.subtotal != null ? Number(e6Data.subtotal) : null,
    montoDelivery: e6Data.monto_delivery != null ? Number(e6Data.monto_delivery) : null,
    total: e6Data.total != null ? Number(e6Data.total) : null,
    itemsCount: e6Data.items_count != null ? Number(e6Data.items_count) : null,
    creadoEn: e6Data.creado_en ?? e6Data.creadoEn ?? null,
    items: [],
  };
}

/**
 * @param {Object} raw
 * @returns {PedidoConfirmacionView|null}
 */
export function mapStoredPedidoToView(raw) {
  if (!raw?.pedido_id) {
    return null;
  }

  const numeroOperativoRaw = raw.numero_operativo ?? raw.codigo_pedido ?? null;
  const numeroOperativo =
    numeroOperativoRaw != null &&
    Number.isFinite(Number(numeroOperativoRaw)) &&
    Number(numeroOperativoRaw) > 0 &&
    !pareceUuid(numeroOperativoRaw)
      ? Number(numeroOperativoRaw)
      : null;
  const codigoPedido =
    numeroOperativo != null ? String(numeroOperativo) : null;

  return {
    pedidoId: raw.pedido_id,
    codigoPedido,
    numeroOperativo,
    estado: raw.estado ?? null,
    canal: raw.canal ?? null,
    subtotal: raw.subtotal != null ? Number(raw.subtotal) : null,
    montoDelivery: raw.monto_delivery != null ? Number(raw.monto_delivery) : null,
    total: raw.total != null ? Number(raw.total) : null,
    itemsCount: raw.items_count != null ? Number(raw.items_count) : null,
    creadoEn: raw.creado_en ?? null,
    items: Array.isArray(raw.items) ? raw.items : [],
  };
}

/**
 * @param {CarritoItem[]|null|undefined} carrito
 * @returns {{ nombre: string, cantidad: number, comentario: string|null, extras: { nombre: string }[] }[]}
 */
export function snapshotItemsPedido(carrito) {
  return (carrito || []).map((item) => ({
    nombre: String(item?.nombre || "").trim(),
    cantidad: Number(item?.cantidad) > 0 ? Number(item.cantidad) : 1,
    comentario: item?.comentario ? String(item.comentario) : null,
    extras: (item?.extras || [])
      .map((extra) => ({
        nombre: String(extra?.nombre || "").trim(),
      }))
      .filter((extra) => extra.nombre),
  })).filter((item) => item.nombre);
}

/**
 * Persiste el pedido confirmado (respuesta E6) para confirmación y tracking.
 *
 * @param {PedidoConfirmacionView} pedido
 * @param {CarritoItem[]|null} [carrito]
 */
export function guardarUltimoPedidoLocal(pedido, carrito = null) {
  if (!pedido?.pedidoId) {
    return;
  }

  const items =
    Array.isArray(pedido.items) && pedido.items.length
      ? pedido.items
      : snapshotItemsPedido(carrito);

  try {
    sessionStorage.setItem(
      STORAGE_ULTIMO_PEDIDO,
      JSON.stringify({
        pedido_id: pedido.pedidoId,
        codigo_pedido: pedido.codigoPedido ?? null,
        numero_operativo: pedido.numeroOperativo ?? null,
        estado: pedido.estado ?? null,
        canal: pedido.canal ?? null,
        subtotal: pedido.subtotal ?? null,
        monto_delivery: pedido.montoDelivery ?? null,
        total: pedido.total ?? null,
        items_count: pedido.itemsCount ?? null,
        creado_en: pedido.creadoEn ?? null,
        items,
      })
    );
  } catch {
    // sessionStorage no disponible
  }
}

/**
 * @returns {PedidoConfirmacionView|null}
 */
export function leerUltimoPedidoLocal() {
  try {
    const raw = sessionStorage.getItem(STORAGE_ULTIMO_PEDIDO);
    if (!raw) {
      return null;
    }
    return mapStoredPedidoToView(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * @param {string} pedidoId
 * @returns {PedidoConfirmacionView|null}
 */
export function leerPedidoLocalPorId(pedidoId) {
  const pedido = leerUltimoPedidoLocal();
  if (!pedido || pedido.pedidoId !== pedidoId) {
    return null;
  }
  return pedido;
}

/**
 * @param {Object|null|undefined} row
 * @returns {null}
 */
export function mapPedidoToView(row) {
  if (!row) {
    return null;
  }

  throw new Error("mapPedidoToView: pendiente implementación tracking");
}
